import React, { useRef, useState } from "react";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import {
  reorderDrawerColumnsByDrag,
  isDrawerLockedColumn,
  isDrawerVisibleColumn,
  isDrawerNonDraggable,
  isAlwaysCheckedColumn,
} from "./applicationListingColumnDnD";

const DRAG_AUTO_SCROLL_ZONE = 48;
const DRAG_AUTO_SCROLL_SPEED = 14;

const ApplicationListingColumnSelector = ({
  columns,
  setColumns,
  defaultColumns,
  onSaveTable,
  onPersistColumns,
  onResetColumns,
  columnsDirty = false,
  isSavingColumns = false,
  isResettingColumns = false,
}) => {
  const [openDrawer, setOpenDrawer] = useState(false);
  const [tempColumns, setTempColumns] = useState(columns);
  const [dragColId, setDragColId] = useState(null);
  const [dragOverColId, setDragOverColId] = useState(null);
  const listRef = useRef(null);
  const dragGhostRef = useRef(null);

  const clearDragGhost = () => {
    if (dragGhostRef.current) {
      dragGhostRef.current.remove();
      dragGhostRef.current = null;
    }
  };

  const handleOpenDrawer = () => {
    setTempColumns([...columns]);
    setDragColId(null);
    setDragOverColId(null);
    clearDragGhost();
    setOpenDrawer(true);
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setDragColId(null);
    setDragOverColId(null);
    clearDragGhost();
  };

  const handleToggleColumn = (id) => {
    if (isDrawerLockedColumn(id)) return;
    setTempColumns((prev) => {
      const maxOrder = Math.max(0, ...prev.map((c) => c.orderIndex || 0));
      return prev.map((col) => {
        if (col.id !== id) return col;
        const nextChecked = !col.checked;
        return {
          ...col,
          checked: nextChecked,
          orderIndex:
            nextChecked && !col.orderIndex
              ? maxOrder + 1
              : col.orderIndex,
        };
      });
    });
  };

  const handleSave = async () => {
    const next = [...tempColumns]
      .map((col) =>
        isDrawerLockedColumn(col.id) || isAlwaysCheckedColumn(col.id)
          ? { ...col, checked: true }
          : col,
      )
      .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
    setColumns(next);
    setOpenDrawer(false);
    if (onPersistColumns) {
      await onPersistColumns(next);
    }
  };

  const handleReset = async () => {
    if (!onResetColumns) {
      setTempColumns(defaultColumns.map((col) => ({ ...col })));
      return;
    }
    try {
      const resetColumns = await onResetColumns();
      if (resetColumns) {
        // Parent already applied columns and cleared Save Table dirty flag.
        // Do not call setColumns here — that would re-enable Save Table.
        setTempColumns(resetColumns.map((col) => ({ ...col })));
      }
    } catch {
      // error surfaced by parent
    }
  };

  const displayColumns = [...tempColumns]
    .filter((col) => isDrawerVisibleColumn(col.id))
    .sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));

  const toggleableColumns = displayColumns.filter(
    (col) => !isDrawerLockedColumn(col.id),
  );
  const allChecked = toggleableColumns.every((col) => col.checked);
  const someChecked =
    toggleableColumns.some((col) => col.checked) && !allChecked;

  const handleSelectAll = () => {
    const newChecked = !allChecked;
    setTempColumns((prev) =>
      prev.map((col) =>
        isDrawerLockedColumn(col.id) || isAlwaysCheckedColumn(col.id)
          ? { ...col, checked: true }
          : { ...col, checked: newChecked },
      ),
    );
  };

  const autoScrollListWhileDragging = (clientY) => {
    const container = listRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    if (clientY < rect.top + DRAG_AUTO_SCROLL_ZONE) {
      container.scrollTop -= DRAG_AUTO_SCROLL_SPEED;
    } else if (clientY > rect.bottom - DRAG_AUTO_SCROLL_ZONE) {
      container.scrollTop += DRAG_AUTO_SCROLL_SPEED;
    }
  };

  const handleColumnDragStart = (e, colId) => {
    if (isDrawerNonDraggable(colId)) {
      e.preventDefault();
      return;
    }
    setDragColId(colId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", colId);

    // Show full row (checkbox + name + handle) as drag preview
    const row = e.currentTarget.closest("[data-column-row]");
    if (row) {
      clearDragGhost();
      const ghost = row.cloneNode(true);
      const rowRect = row.getBoundingClientRect();
      ghost.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        width: ${rowRect.width}px;
        opacity: 1 !important;
        background: #b3d9f7;
        box-shadow: 0 8px 20px rgba(0,0,0,0.35);
        border: 2px solid #0569c1;
        border-radius: 4px;
        pointer-events: none;
        z-index: 100000;
        color: #000;
        filter: none;
      `;
      ghost.querySelectorAll("*").forEach((el) => {
        el.style.opacity = "1";
        el.style.filter = "none";
        if (el.classList?.contains("MuiFormControlLabel-label")) {
          el.style.color = "#000";
          el.style.fontWeight = "700";
          el.style.opacity = "1";
        }
        if (el.classList?.contains("MuiCheckbox-root")) {
          el.style.color = "#0569c1";
          el.style.opacity = "1";
          el.style.padding = "4px";
          el.style.transform = "none";
        }
        // Keep checkbox icon at small size (browser drag preview can enlarge it)
        if (
          el.classList?.contains("MuiSvgIcon-root") &&
          el.closest?.(".MuiCheckbox-root")
        ) {
          el.style.width = "18px";
          el.style.height = "18px";
          el.style.fontSize = "18px";
          el.style.transform = "none";
        }
        if (
          (el.tagName === "svg" || el.tagName === "path") &&
          el.closest?.(".MuiCheckbox-root")
        ) {
          el.style.width = "18px";
          el.style.height = "18px";
          el.style.opacity = "1";
          el.style.color = "#0569c1";
        }
      });
      document.body.appendChild(ghost);
      dragGhostRef.current = ghost;

      const offsetX = Math.min(
        Math.max(16, e.clientX - rowRect.left),
        rowRect.width - 16,
      );
      const offsetY = Math.min(
        Math.max(8, e.clientY - rowRect.top),
        rowRect.height - 8,
      );
      e.dataTransfer.setDragImage(ghost, offsetX, offsetY);
    }
  };

  const handleListDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    autoScrollListWhileDragging(e.clientY);
  };

  const handleColumnDragOver = (e, colId) => {
    if (isDrawerNonDraggable(colId)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    autoScrollListWhileDragging(e.clientY);
    if (dragOverColId !== colId) setDragOverColId(colId);
  };

  const handleColumnDrop = (e, dropColId) => {
    e.preventDefault();
    if (isDrawerNonDraggable(dropColId)) {
      setDragColId(null);
      setDragOverColId(null);
      clearDragGhost();
      return;
    }
    const fromId = e.dataTransfer.getData("text/plain") || dragColId;
    if (!fromId || fromId === dropColId) {
      setDragColId(null);
      setDragOverColId(null);
      clearDragGhost();
      return;
    }
    setTempColumns((prev) =>
      reorderDrawerColumnsByDrag(prev, fromId, dropColId),
    );
    setDragColId(null);
    setDragOverColId(null);
    clearDragGhost();
  };

  const handleColumnDragEnd = () => {
    setDragColId(null);
    setDragOverColId(null);
    clearDragGhost();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1.5,
        mt: 2,
      }}
    >
      <Button
        variant="contained"
        onClick={onSaveTable}
        disabled={!columnsDirty || isSavingColumns}
        sx={{
          backgroundColor: columnsDirty ? "#067de2" : "#E9E9E9",
          color: columnsDirty ? "#fff" : "rgba(0, 0, 0, 0.38)",
          textTransform: "none",
          fontWeight: 400,
          fontSize: "14px",
          fontFamily: "proxima-regular",
          boxShadow: "none",
          borderRadius: "2px",
          "&:hover": {
            backgroundColor: columnsDirty ? "#0569c1" : "#E9E9E9",
            boxShadow: "none",
          },
          "&.Mui-disabled": {
            backgroundColor: "#E9E9E9",
            color: "rgba(0, 0, 0, 0.38)",
          },
        }}
      >
        {isSavingColumns ? "Saving..." : "Save Table"}
      </Button>

      <Button
        variant="contained"
        startIcon={<ViewModuleIcon />}
        onClick={handleOpenDrawer}
        sx={{
          backgroundColor: "#E9E9E9",
          color: "#000",
          textTransform: "none",
          fontWeight: 600,
          fontSize: "14px",
          fontFamily: "proxima-bold",
          boxShadow: "none",
          borderRadius: "2px",
          "&:hover": {
            backgroundColor: "#E9E9E9",
            boxShadow: "none",
          },
        }}
      >
        Change Column
      </Button>

      <Drawer
        anchor="right"
        open={openDrawer}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: 300,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            height: 50,
            background: "#067de2",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: "#fff",
              fontFamily: "proxima-bold",
            }}
          >
            Select Your Columns
          </Typography>

          <IconButton
            size="small"
            onClick={handleCloseDrawer}
            sx={{
              color: "#fff",
              p: 0,
            }}
          >
            <CancelOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box
          ref={listRef}
          onDragOver={handleListDragOver}
          sx={{
            flex: 1,
            overflowY: "auto",
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 0.5,
              borderBottom: "1px solid #E9E9E9",
              mb: 0.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={someChecked}
                  onChange={handleSelectAll}
                  sx={{
                    color: "#067de2",
                    "&.Mui-checked": { color: "#067de2" },
                    "&.MuiCheckbox-indeterminate": { color: "#067de2" },
                  }}
                />
              }
              label={allChecked ? "Unselect All" : "Select All"}
              sx={{
                flex: 1,
                ml: 0,
                mr: 0,
                minWidth: 0,
                "& .MuiFormControlLabel-label": {
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#000",
                  fontFamily: "proxima-bold",
                },
              }}
            />
            {/* Spacer so Select All lines up with rows that have a drag handle */}
            <Box sx={{ width: 24, flexShrink: 0 }} aria-hidden="true" />
          </Box>
          {displayColumns.map((col) => {
            const isLocked = isDrawerLockedColumn(col.id);
            const canDrag = !isDrawerNonDraggable(col.id);
            const isDragOver =
              dragOverColId === col.id && dragColId && dragColId !== col.id;
            return (
              <Box
                key={col.id}
                data-column-row
                onDragOver={(e) => handleColumnDragOver(e, col.id)}
                onDrop={(e) => handleColumnDrop(e, col.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 0.5,
                  py: 0.25,
                  opacity: dragColId === col.id ? 0.9 : 1,
                  outline: isDragOver ? "2px solid #067de2" : "none",
                  borderRadius: "2px",
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={isLocked ? true : col.checked}
                      disabled={isLocked}
                      onChange={() => handleToggleColumn(col.id)}
                      onMouseDown={(e) => e.stopPropagation()}
                      sx={
                        isLocked
                          ? {
                              opacity: 1,
                              pointerEvents: "none",
                              cursor: "default",
                              color: "#8ec5f0",
                              "&.Mui-checked": {
                                color: "#8ec5f0",
                              },
                              "&.Mui-disabled": {
                                opacity: 1,
                                color: "#8ec5f0",
                              },
                              "&.Mui-checked.Mui-disabled": {
                                opacity: 1,
                                color: "#8ec5f0",
                              },
                              "&.Mui-disabled .MuiSvgIcon-root": {
                                opacity: 1,
                              },
                            }
                          : {
                              color: "#067de2",
                              "&.Mui-checked": {
                                color: "#067de2",
                              },
                            }
                      }
                    />
                  }
                  label={col.label}
                  sx={{
                    flex: 1,
                    ml: 0,
                    mr: 0,
                    minWidth: 0,
                    "& .MuiFormControlLabel-label": {
                      fontSize: "14px",
                      color: "#000",
                      fontFamily: "proxima-regular",
                    },
                    "& .MuiFormControlLabel-label.Mui-disabled": {
                      color: "#000",
                    },
                  }}
                />
                <Box
                  component="span"
                  draggable={canDrag}
                  aria-label={
                    canDrag ? `Drag to reorder ${col.label}` : undefined
                  }
                  onDragStart={(e) => handleColumnDragStart(e, col.id)}
                  onDragEnd={handleColumnDragEnd}
                  onMouseDown={(e) => e.stopPropagation()}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    flexShrink: 0,
                    cursor: canDrag ? "grab" : "default",
                    color: canDrag ? "#bdbdbd" : "#e8e8e8",
                    transition: "color 0.2s ease",
                    touchAction: "none",
                    "&:hover": canDrag
                      ? {
                          color: "#067de2",
                        }
                      : undefined,
                    "&:active": {
                      cursor: canDrag ? "grabbing" : "default",
                    },
                  }}
                >
                  <DragIndicatorIcon sx={{ fontSize: 20 }} />
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ py: 2, px: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              pr: 1,
            }}
          >
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={isSavingColumns || isResettingColumns}
              sx={{
                minWidth: 65,
                height: 30,
                borderRadius: "15px",
                fontSize: 12,
                fontFamily: "proxima-regular",
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              {isSavingColumns ? "Saving..." : "Save"}
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={handleReset}
              disabled={isSavingColumns || isResettingColumns}
              sx={{
                minWidth: 65,
                height: 30,
                borderRadius: "15px",
                fontSize: 12,
                fontFamily: "proxima-regular",
                textTransform: "none",
                boxShadow: "none",
              }}
            >
              {isResettingColumns ? "Resetting..." : "Reset"}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default ApplicationListingColumnSelector;
